import { type FieldsetHTMLAttributes, useEffect, useId, useState } from 'react';
import IconCheck from '~/vendor/cxy/icons/icons/Check';
import * as RadixCheckbox from '@radix-ui/react-checkbox';
import clsx from 'clsx';

import { Label } from '../Label';

import styles from './styles/Checkbox.module.scss';

export type FieldsetProps = FieldsetHTMLAttributes<HTMLFieldSetElement>;
export type CheckboxProps = RadixCheckbox.CheckboxProps;

export const CheckboxGroup = ({ children, className, label, ...props }: { label?: string } & FieldsetProps) => {
  if (!label && !props['aria-label'] && !props['aria-labelledby']) {
    console.warn(
      'Checkbox group must provide and `aria-label` if no visible `label` prop is provided. If you do not provide a visible label, you must specify an aria-label or aria-labelledby attribute for accessibility',
    );
  }

  return (
    // `...props` last: the type accepts every fieldset attribute, and only three were forwarded, so
    // `disabled` (which should disable every control in the group), `name` and `id` were dropped. The
    // two aria props are computed above from `label`, so they are spread first and then overridden
    // deliberately when the caller set them and there is no visible label.
    <fieldset
      className={clsx(styles.checkboxGroup, className)}
      {...props}
      aria-label={!label ? props['aria-label'] : undefined}
      aria-labelledby={!label ? props['aria-labelledby'] : undefined}
    >
      {label && <legend>{label}</legend>}
      {children}
    </fieldset>
  );
};

export const Checkbox = ({
  checked,
  children,
  className,
  onCheckedChange,
  shouldHighlightOnChecked = true,
  ...props
}: { shouldHighlightOnChecked?: boolean } & CheckboxProps) => {
  const [isSelected, setIsSelected] = useState(checked);
  const id = useId();

  useEffect(() => {
    setIsSelected(checked);
  }, [checked]);

  return (
    <Label
      className={clsx(
        styles.checkbox,
        {
          [styles.checked]: isSelected,
          [styles.shouldHighlightOnChecked]: shouldHighlightOnChecked,
        },
        className,
      )}
      data-cxy-name="checkbox"
      id={id}
    >
      <RadixCheckbox.Root
        aria-labelledby={id}
        {...props}
        checked={isSelected}
        className={styles.checkboxBox}
        onCheckedChange={e => {
          onCheckedChange?.(e);
          setIsSelected(e);
        }}
      >
        <RadixCheckbox.Indicator className={styles.indicator}>
          <IconCheck color="blue" size="xsm" />
        </RadixCheckbox.Indicator>
      </RadixCheckbox.Root>
      {children}
    </Label>
  );
};
